import axios from 'axios';
import _ from 'lodash';
import Vue from 'vue';
import API_CONFIG from '@/config';
import router from '../router';
import store from '../store';
/**
 * @var{string} LOGIN_URL The endpoint for logging in. This endpoint should be proxied by Webpack dev server
 *    and maybe nginx in production (cleaner calls and avoids CORS issues).
 */
const LOGIN_URL = `${API_CONFIG.ADMIN_API}/oauth/token`;

/**
 * @var{string} REFRESH_TOKEN_URL The endpoint for refreshing an access_token. This endpoint should be proxied
 *    by Webpack dev server and maybe nginx in production (cleaner calls and avoids CORS issues).
 */
const REFRESH_TOKEN_URL = `${API_CONFIG.ADMIN_API}/oauth/token`;

const AUTH_REFRESH_HEADERS = {
  headers: {
    'Content-Type': 'form-data',
  },
};

export const currentVue = Vue;

/**
 * Auth Plugin
 *
 * (see https://vuejs.org/v2/guide/plugins.html for more info on Vue.js plugins)
 *
 * Handles login and token authentication using OAuth2.
 */
export default {

  /**
   * Install the Auth class.
   *
   * Creates a Vue-resource http interceptor to handle automatically adding auth headers
   * and refreshing tokens. Then attaches this object to the global Vue (as Vue.auth).
   *
   * @param {Object} Vue The global Vue.
   * @param {Object} options Any options we want to have in our plugin.
   * @return {void}
   */
  install(vue) {
    if (this.installed && currentVue === vue) return;
    this.installed = true;

    this.baseHttp = axios.create({
      baseURL: API_CONFIG.API,
      headers: {
        Accept: 'application/vnd.api+json',
        'Content-Type': 'application/vnd.api+json',
      },
    });

    this.baseHttp.interceptors.request.use((config) => {
      const token = store.state.auth.accessToken;
      const hasAuthHeader = config.headers.Authorization !== undefined;
      if (token && !hasAuthHeader) {
        this.setAuthHeader(config);
      }
      return config;
    });

    /* this.baseHttp.interceptors.response.use(response => response, (error) => {
      // TODO handle when token session is finish
      // if (this._isInvalidToken(error.response)) {
      //   return this._refreshToken(error.config);
      // }
      return error.response;
    }); */

    Vue.prototype.$axios = this;
    Vue.$axios = this;
  },

  /**
   * Login
   *
   * @param {Object.<string>} creds The username and password for logging in.
   * @param {string|null} redirect The name of the Route to redirect to.
   * @return {Promise}
   */
  async login(creds) {
    const params = new URLSearchParams();
    params.append('grant_type', 'password');
    params.append('username', creds.username);
    params.append('password', creds.password);
    params.append('client_id', API_CONFIG.AUTH_ID_CLIENTE);
    params.append('client_secret', API_CONFIG.AUTH_PASS_ID_CLIENTE);
    function storeToken(response) {
      const auth = store.state.auth;

      auth.isLoggedIn = true;
      auth.accessToken = response.data.access_token;
      auth.refreshToken = response.data.refresh_token;

      store.commit('UPDATE_AUTH', auth);
    }
    async function loginCallback(response) {
      storeToken(response);
      const promises = [
        store.dispatch('user_model/fetchAction', {}),
        store.dispatch('employee_model/fetchAction', {}),
        store.dispatch('taxonomy_department/fetchAction', {}),
        store.dispatch('taxonomy/updateTaxonomiesAction', {}),
        store.dispatch('documents/loadDocumentTypesAction', {}),
      ];
      await Promise.all(promises);
      const users = await store.getters['user_model/entitiesGetter'];
      const employees = await store.getters['employee_model/entitiesGetter'];
      const departments = await store.getters['taxonomy_department/entitiesGetter'];
      let user = null;
      _.forOwn(users, (u) => {
        if (u.name === creds.username) {
          user = _.clone(u);
          _.forOwn(employees, (e) => {
            if (e.user === user.id) {
              user.employee = _.cloneDeep(e);
              user.employee.department = user.employee.department ? _.cloneDeep(departments[user.employee.department]) : null;
            }
          });
        }
      });
      store.commit('UPDATE_USER', user);
      return response;
    }
    return axios.post(LOGIN_URL, params, { 'Content-Type': 'application/x-www-form-urlencoded' })
      .then(loginCallback)
      .catch(errorResponse => errorResponse);
  },
  http() {
    return this.baseHttp;
  },
  get(url) {
    return this.baseHttp.get(url);
  },
  post(url, payload) {
    return this.baseHttp.post(url, payload);
  },
  patch(url, payload) {
    return this.baseHttp.patch(url, payload);
  },
  'delete'(url) { // eslint-disable-line
    return this.baseHttp.delete(url);
  },
  /**
   * Logout
   *
   * Clear all data in our Vuex store (which resets logged-in status) and redirect back
   * to login form.
   *
   * @return {void}
   */
  logout() {
    router.push({ name: 'login' }, () => store.commit('CLEAR_ALL_DATA'));
  },

  /**
   * Set the Authorization header on a Vue-resource Request.
   *
   * @param {Request} request The Vue-Resource Request instance to set the header on.
   * @return {void}
   */
  setAuthHeader(request) {
    request.headers.get.Authorization = `Bearer ${store.state.auth.accessToken}`;
    request.headers.post.Authorization = `Bearer ${store.state.auth.accessToken}`;
    request.headers.patch.Authorization = `Bearer ${store.state.auth.accessToken}`;
    request.headers.delete.Authorization = `Bearer ${store.state.auth.accessToken}`;
  },

  /**
   * Retry the original request.
   *
   * Let's retry the user's original target request that had recieved a invalid token response
   * (which we fixed with a token refresh).
   *
   * @param {Request} request The Vue-resource Request instance to use to repeat an http call.
   * @return {Promise}
   */
  _retry(request) {
    this.setAuthHeader(request);

    return Vue.http(request)
      .then(response => response)
      .catch(response => response);
  },

  /**
   * Refresh the access token
   *
   * Make an ajax call to the OAuth2 server to refresh the access token (using our refresh token).
   *
   * @private
   * @param {Request} request Vue-resource Request instance, the original request that we'll retry.
   * @return {Promise}
   */
  _refreshToken(request) {
    const params = {
      grant_type: 'refresh_token',
      client_id: API_CONFIG.AUTH_ID_CLIENTE,
      client_secret: API_CONFIG.AUTH_PASS_ID_CLIENTE,
      refresh_token: store.state.auth.refreshToken,
    };

    return Vue.http.post(REFRESH_TOKEN_URL, params, AUTH_REFRESH_HEADERS)
      .then((result) => {
        this._storeToken(result);
        return this._retry(request);
      })
      .catch((errorResponse) => {
        if (this._isInvalidToken(errorResponse)) {
          this.logout();
        }
        return errorResponse;
      });
  },

  /**
   * Check if the Vue-resource Response is an invalid token response.
   *
   * @private
   * @param {Response} response The Vue-resource Response instance received from an http call.
   * @return {boolean}
   */
  _isInvalidToken(response) {
    const status = response.status;
    const error = response.data.error;

    return (status === 401 && (error === 'invalid_token' || error === 'expired_token'));
  },
};




// WEBPACK FOOTER //
// ./src/plugins/baseHttp.js